import { useEffect } from 'react'

export type PageMeta = {
  title: string
  description?: string | null
  /** The address search engines should treat as this page's own. Defaults to the current address without a query. */
  canonicalUrl?: string
  /** Asks search engines not to list this page. */
  noIndex?: boolean
  /** Structured data (schema.org) to publish with the page. */
  jsonLd?: object | null
}

function setMeta(name: string, content: string | null): () => void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  const previous = existing?.getAttribute('content') ?? null
  const el = existing ?? document.createElement('meta')
  if (!existing) {
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  if (content === null) el.removeAttribute('content')
  else el.setAttribute('content', content)
  return () => {
    if (!existing) el.remove()
    else if (previous === null) el.removeAttribute('content')
    else el.setAttribute('content', previous)
  }
}

/**
 * Sets the page title, description, canonical link, robots and structured data while the page is shown, and puts
 * them back afterwards. These are set in the browser, so they count for search engines that run a page's scripts.
 */
export function usePageMeta({ title, description, canonicalUrl, noIndex, jsonLd }: PageMeta) {
  const json = jsonLd ? JSON.stringify(jsonLd) : null

  useEffect(() => {
    const previousTitle = document.title
    document.title = title

    const undo: (() => void)[] = []
    if (description) undo.push(setMeta('description', description))
    undo.push(setMeta('robots', noIndex ? 'noindex, follow' : 'index, follow'))

    const href = canonicalUrl ?? `${window.location.origin}${window.location.pathname}`
    const link = document.createElement('link')
    link.rel = 'canonical'
    link.href = href
    document.head.appendChild(link)
    undo.push(() => link.remove())

    if (json) {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.text = json
      document.head.appendChild(script)
      undo.push(() => script.remove())
    }

    return () => {
      document.title = previousTitle
      undo.forEach(fn => fn())
    }
  }, [title, description, canonicalUrl, noIndex, json])
}
